var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import CredoExemptionUtils from "./pages/util/credo-exemption-utils";
import _ from "underscore";
var ExemptAction;
(function (ExemptAction) {
    ExemptAction[ExemptAction["CREATE"] = 0] = "CREATE";
    ExemptAction[ExemptAction["EXTEND"] = 1] = "EXTEND";
    ExemptAction[ExemptAction["CANCEL"] = 2] = "CANCEL";
    ExemptAction[ExemptAction["DISPLAY"] = 3] = "DISPLAY";
})(ExemptAction || (ExemptAction = {}));
var CredoExemptionStateContext = React.createContext(undefined);
var CredoExemptionDispatchContext = React.createContext(undefined);
var credoExemptionReducer = function (state, action) {
    switch (action.type) {
        case 'setPage': {
            return __assign(__assign({}, state), { page: action.data.value });
        }
        case 'setOpen': {
            var _actual_modal = state.metaData.action === null ? false : action.data.value;
            return __assign(__assign({}, state), { isOpen: _actual_modal });
        }
        case 'setExemptionAction': {
            // View and cancel is from display page
            var _page = 1;
            if (action.data.value in [ExemptAction.EXTEND, ExemptAction.CREATE]) {
                _page = 0;
            }
            return __assign(__assign({}, state), { exemptionAction: action.data.value, page: _page });
        }
        case 'setJustification': {
            return __assign(__assign({}, state), { justification: action.data.value });
        }
        case 'setExtendFromTicket': {
            return __assign(__assign({}, state), { extendFromTicket: action.data.value });
        }
        case "setCredoExemptionData": {
            var _data = action.data.value;
            if (!_data) {
                return __assign({}, state);
            }
            var _exemptionData = state.credoExemptionData || Array(0);
            if (_data.status.toUpperCase() === "OPEN") {
                _exemptionData.push(_data);
            }
            else if (_data.status.toUpperCase() === "CANCELLED") {
                var _key_1 = _data.key;
                _.filter(_exemptionData, function (item) {
                    return item.key === _key_1;
                })[0].status = _data.status;
            }
            var _metaData = CredoExemptionUtils.evaluateExemptionBasedOnExistingMetaData(_exemptionData, state.metaData, state.tier);
            var _page = ((_metaData.action || ExemptAction.CREATE) in [ExemptAction.CREATE, ExemptAction.EXTEND]) ? 0 : 1;
            return __assign(__assign({}, state), { credoExemptionData: _exemptionData, metaData: _metaData, page: _page, exemptionAction: (_metaData.action || ExemptAction.CREATE) });
        }
        default: {
            throw new Error("Unhandled action: " + action);
        }
    }
};
// useLocallyPersistedReducer uses localStorage to save react state between usages
// NOTE: it does not handle schema changing of the defaultState; update CREDO_STORAGE_KEY version
var useLocalReducer = function (reducer, defaultState, storageKey) {
    var hookVars = React.useReducer(reducer, defaultState, function (defaultState) {
        var persisted = JSON.parse(localStorage.getItem(storageKey) || '{}');
        return Object.keys(persisted).length === 0 && persisted.constructor === Object
            ? defaultState
            : persisted;
    });
    // Don't copy the default state isOpen status, as it will impact the actual modal behaviour
    hookVars[0].credoExemptionData = defaultState.credoExemptionData;
    hookVars[0].extendFromTicket = defaultState.extendFromTicket;
    hookVars[0].metaData = defaultState.metaData;
    hookVars[0].tier = defaultState.tier;
    React.useEffect(function () {
        localStorage.setItem(storageKey, JSON.stringify(hookVars[0]));
    }, [storageKey, hookVars[0]]);
    return hookVars;
};
var CredoExemptionProvider = function (_a) {
    var credoExemptionFF = _a.credoExemptionFF, servicedne = _a.servicedne, complianceComplete = _a.complianceComplete, soc2 = _a.soc2, children = _a.children, credoExemptionData = _a.credoExemptionData, credo = _a.credo, service = _a.service;
    var metaData = CredoExemptionUtils.evaluateExemption(credoExemptionFF, servicedne, complianceComplete, credo, credoExemptionData, service);
    var tier = service.metadata.sla.serviceTier;
    var serviceId = service.id;
    var extendFromTicket = '', justification = '';
    if (credoExemptionData && credoExemptionData.length > 0) {
        // Taking the first as most recent as exemption list won't have any cancel status ticket.
        var _expired_exemption = _.find(credoExemptionData, function (exemption) { return exemption.status.toUpperCase() === "EXPIRED" && exemption.tier === tier; });
        if (_expired_exemption) {
            extendFromTicket = _expired_exemption.parent ? _expired_exemption.parent : _expired_exemption.key;
            justification = _expired_exemption.justification;
        }
    }
    var exemptionAction = metaData.action || ExemptAction.CREATE;
    var page = (exemptionAction in [ExemptAction.CREATE, ExemptAction.EXTEND]) ? 0 : 1;
    var isOpen = false;
    var defaultState = {
        isOpen: isOpen,
        page: page,
        tier: tier,
        serviceId: serviceId,
        soc2: soc2,
        credoExemptionData: credoExemptionData,
        exemptionAction: exemptionAction,
        justification: justification,
        extendFromTicket: extendFromTicket,
        metaData: metaData
    };
    var storageKey = "credoExemptionState-" + serviceId;
    // save the state per serviceId with the version + serviceId
    var _b = useLocalReducer(credoExemptionReducer, defaultState, storageKey), state = _b[0], dispatch = _b[1];
    return (React.createElement(CredoExemptionStateContext.Provider, { value: state },
        React.createElement(CredoExemptionDispatchContext.Provider, { value: dispatch }, children)));
};
var useCredoExemptionState = function () {
    var context = React.useContext(CredoExemptionStateContext);
    if (context === undefined) {
        throw new Error('useCredoExemptionState must be used within a CredoExemptionProvider');
    }
    return context;
};
var useCredoExemptionDispatch = function () {
    var context = React.useContext(CredoExemptionDispatchContext);
    if (context === undefined) {
        throw new Error('useCredoExemptionDispatch must be used within a CredoExemptionProvider');
    }
    return context;
};
export { CredoExemptionProvider, useCredoExemptionState, useCredoExemptionDispatch, ExemptAction };
